import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchMe } from "../services/meService";
import { changePassword } from "../services/accountService";
import { createCheckoutSession, createPortalSession } from "../services/billingService";

const toNum = (value, fallback = 0) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || fallback;
};

const firstObject = (...values) =>
  values.find((value) => value && typeof value === "object" && !Array.isArray(value)) || null;

const mapPlanCodeToTier = (planCode) => {
  const p = String(planCode || "").toLowerCase();
  if (p.startsWith("agency")) return "Agency";
  if (p.startsWith("growth")) return "Growth";
  if (p.startsWith("enterprise")) return "Enterprise";
  return "Free";
};

const getPlanCodeFromMe = (me) =>
  me?.subscription?.plan_code ??
  me?.subscription?.planCode ??
  me?.entitlements?.plan_code ??
  me?.entitlements?.planCode ??
  me?.identity?.plan ??
  me?.identity?.plan_code ??
  "free";

const getUsageFromMe = (me) => {
  const u =
    me?.apiUsage ??
    me?.api_usage ??
    me?.usage ??
    me?.entitlements?.apiUsage ??
    me?.entitlements?.api_usage ??
    me?.entitlements?.usage ??
    me?.subscription?.usage ??
    null;

  const monthly_limit =
    toNum(u?.monthly_limit ?? u?.monthlyLimit ?? u?.limit ?? u?.monthly ?? 0, 0);

  const current_usage =
    toNum(u?.current_usage ?? u?.currentUsage ?? u?.used ?? u?.current ?? 0, 0);

  return { monthly_limit, current_usage };
};

const getSearchCreditsFromMe = (me) => {
  const direct =
    me?.searchCredits ??
    me?.search_credits ??
    me?.entitlements?.searchCredits ??
    me?.entitlements?.search_credits ??
    me?.entitlements?.credits ??
    me?.subscription?.searchCredits ??
    me?.subscription?.search_credits ??
    me?.identity?.searchCredits ??
    me?.identity?.search_credits;

  if (direct !== undefined && direct !== null) {
    return toNum(direct, 0);
  }

  const usageBased =
    me?.entitlements?.usage?.search_credits?.used ??
    me?.entitlements?.usage?.searchCredits?.used ??
    me?.entitlements?.usage?.search_credits ??
    me?.entitlements?.usage?.searchCredits ??
    0;

  return toNum(usageBased, 0);
};

const normalizeMeForState = (me) => {
  if (!me || typeof me !== "object") {
    return {
      identity: null,
      profile: null,
      profile_details: null,
      subscription: null,
      entitlements: null,
    };
  }

  return {
    identity: firstObject(me?.identity) ?? null,
    profile: firstObject(me?.profile, me?.userProfile, me?.user_profile) ?? null,
    profile_details:
      firstObject(
        me?.profile_details,
        me?.profileDetails,
        me?.details,
        me?.profile?.details,
        me?.profile?.profile_details,
        me?.profile?.profileDetails
      ) ?? null,
    subscription:
      firstObject(me?.subscription, me?.billing, me?.subscription_details) ?? null,
    entitlements:
      firstObject(me?.entitlements, me?.permissions, me?.plan_entitlements) ?? null,
  };
};

const initialState = {
  me: {
    identity: null,
    profile: null,
    profile_details: null,
    subscription: null,
    entitlements: null,
  },

  subscriptionTier: "Free",
  searchCredits: 0,

  apiUsage: {
    monthly_limit: 0,
    current_usage: 0,
  },

  loading: false,
  changingPassword: false,
  upgradingSubscription: false,
  openingBillingPortal: false,

  error: null,
  success: null,
};

export const fetchAccountSettingsAsync = createAsyncThunk(
  "accountSettings/fetchAccountSettings",
  async (_, { rejectWithValue }) => {
    try {
      const me = await fetchMe();
      return { me };
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Error fetching account settings."));
    }
  }
);

export const changePasswordAsync = createAsyncThunk(
  "accountSettings/changePassword",
  async (payload, { rejectWithValue }) => {
    try {
      await changePassword(payload);
      return true;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Error updating password."));
    }
  }
);

export const upgradeSubscriptionAsync = createAsyncThunk(
  "accountSettings/upgradeSubscription",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createCheckoutSession(payload);
      const checkoutUrl = res?.checkout_url || res?.checkoutUrl || res?.url;

      if (!checkoutUrl) {
        return rejectWithValue("Checkout URL missing from server response.");
      }

      window.location.assign(checkoutUrl);
      return { redirected: true, session_id: res?.session_id ?? res?.sessionId ?? null };
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Error upgrading subscription."));
    }
  }
);

export const openBillingPortalAsync = createAsyncThunk(
  "accountSettings/openBillingPortal",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createPortalSession(payload || {});
      const portalUrl = res?.portal_url || res?.portalUrl || res?.url;

      if (!portalUrl) {
        return rejectWithValue("Portal URL missing from server response.");
      }

      window.location.assign(portalUrl);
      return { redirected: true };
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, "Error opening billing portal."));
    }
  }
);

const accountSettingsSlice = createSlice({
  name: "accountSettings",
  initialState,
  reducers: {
    clearAccountMessages(state) {
      state.error = null;
      state.success = null;
    },

    clearMeState(state) {
      state.me = {
        identity: null,
        profile: null,
        profile_details: null,
        subscription: null,
        entitlements: null,
      };
      state.subscriptionTier = "Free";
      state.searchCredits = 0;
      state.apiUsage = { monthly_limit: 0, current_usage: 0 };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountSettingsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchAccountSettingsAsync.fulfilled, (state, action) => {
        state.loading = false;

        const me = action.payload?.me || null;
        const normalized = normalizeMeForState(me);

        state.me.identity = normalized.identity;
        state.me.profile = normalized.profile;
        state.me.profile_details = normalized.profile_details;
        state.me.subscription = normalized.subscription;
        state.me.entitlements = normalized.entitlements;

        state.subscriptionTier = mapPlanCodeToTier(getPlanCodeFromMe(me));
        state.searchCredits = getSearchCreditsFromMe(me);
        state.apiUsage = getUsageFromMe(me);

        state.error = null;
      })
      .addCase(fetchAccountSettingsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching account settings.";
      })

      .addCase(changePasswordAsync.pending, (state) => {
        state.changingPassword = true;
        state.error = null;
        state.success = null;
      })
      .addCase(changePasswordAsync.fulfilled, (state) => {
        state.changingPassword = false;
        state.success = "Password updated successfully!";
      })
      .addCase(changePasswordAsync.rejected, (state, action) => {
        state.changingPassword = false;
        state.error = action.payload || "Error updating password.";
      })

      .addCase(upgradeSubscriptionAsync.pending, (state) => {
        state.upgradingSubscription = true;
        state.error = null;
        state.success = null;
      })
      .addCase(upgradeSubscriptionAsync.fulfilled, (state) => {
        state.upgradingSubscription = false;
      })
      .addCase(upgradeSubscriptionAsync.rejected, (state, action) => {
        state.upgradingSubscription = false;
        state.error = action.payload || "Error upgrading subscription.";
      })

      .addCase(openBillingPortalAsync.pending, (state) => {
        state.openingBillingPortal = true;
        state.error = null;
        state.success = null;
      })
      .addCase(openBillingPortalAsync.fulfilled, (state) => {
        state.openingBillingPortal = false;
      })
      .addCase(openBillingPortalAsync.rejected, (state, action) => {
        state.openingBillingPortal = false;
        state.error = action.payload || "Error opening billing portal.";
      });
  },
});

export const { clearAccountMessages, clearMeState } = accountSettingsSlice.actions;

export const selectMe = (state) => state.accountSettings?.me || {};
export const selectMeIdentity = (state) => state.accountSettings?.me?.identity || null;
export const selectMeProfile = (state) => state.accountSettings?.me?.profile || null;
export const selectMeProfileDetails = (state) =>
  state.accountSettings?.me?.profile_details || null;
export const selectMeSubscription = (state) =>
  state.accountSettings?.me?.subscription || null;
export const selectMeEntitlements = (state) =>
  state.accountSettings?.me?.entitlements || null;

export const selectSubscriptionTier = (state) =>
  state.accountSettings?.subscriptionTier || "Free";
export const selectSearchCredits = (state) =>
  state.accountSettings?.searchCredits ?? 0;

export const selectAccountApiUsage = (state) =>
  state.accountSettings?.apiUsage || { monthly_limit: 0, current_usage: 0 };

export const selectAccountSettingsLoading = (state) =>
  state.accountSettings?.loading || false;
export const selectChangingPassword = (state) =>
  state.accountSettings?.changingPassword || false;
export const selectUpgradingSubscription = (state) =>
  state.accountSettings?.upgradingSubscription || false;
export const selectOpeningBillingPortal = (state) =>
  state.accountSettings?.openingBillingPortal || false;

export const selectAccountError = (state) =>
  state.accountSettings?.error || null;
export const selectAccountSuccess = (state) =>
  state.accountSettings?.success || null;

export default accountSettingsSlice.reducer;